import React, { useState, useRef, useEffect } from 'react';
import { BsSearch, BsChevronDown } from 'react-icons/bs';
import styles from './Select.module.css';

const Select = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRef = useRef([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOptionClick = (option) => {
    const newValue = value.some(v => v.value === option.value)
      ? value.filter(v => v.value !== option.value)
      : [...value, option];
    
    if (option.value === 'all') {
      onChange([option]);
    } else {
      const withoutAll = newValue.filter(v => v.value !== 'all');
      onChange(withoutAll);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleOptionClick(filteredOptions[focusedIndex]);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0 && optionsRef.current[focusedIndex]) {
      optionsRef.current[focusedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [focusedIndex]);

  return (
    <div 
      className={styles['cls-select']} 
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      tabIndex="0"
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-controls="select-dropdown"
    >
      <div 
        className={styles['cls-select-control']} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles['cls-select-values']} data-placeholder={placeholder}>
          {value.length > 0 
            ? value.map(v => v.label).join(', ') 
            : placeholder}
        </div>
        <BsChevronDown className={`${styles['cls-select-arrow']} ${isOpen ? styles['cls-select-arrow--open'] : ''}`} />
      </div>
      
      {isOpen && (
        <div 
          id="select-dropdown"
          className={styles['cls-select-dropdown']}
          role="listbox"
        >
          <div className={styles['cls-select-search']}>
            <BsSearch className={styles['cls-select-search-icon']} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className={styles['cls-select-search-input']}
              aria-label="Seçeneklerde ara"
            />
          </div>
          <div className={styles['cls-select-options']}>
            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                ref={el => optionsRef.current[index] = el}
                className={styles['cls-select-option']}
                onClick={() => handleOptionClick(option)}
                role="option"
                aria-selected={value.some(v => v.value === option.value)}
                tabIndex="-1"
                style={{
                  backgroundColor: focusedIndex === index ? 'rgba(99, 102, 241, 0.05)' : undefined
                }}
              >
                <input
                  type="checkbox"
                  checked={value.some(v => v.value === option.value)}
                  onChange={() => {}}
                  className={styles['cls-select-checkbox']}
                  aria-label={`Seç ${option.label}`}
                />
                <span className={styles['cls-select-label']}>{option.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Select; 